// src/pages/ViewNotices.tsx
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import {
  Box,
  Typography,
  Alert,
  Fab,
  useTheme,
  useMediaQuery,
  Drawer,
  Paper,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import EventIcon from "@mui/icons-material/Event";
import ImageIcon from "@mui/icons-material/Image";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { EmptyState } from "../components/shared";
import { useSnackbar } from "../context/SnackbarContext";
import { buildManualReminderNoticePayload } from "../utils/noticeManualReminder";
import { buildDraftRestorePayload } from "../utils/draftRestore";
import { buildNoticeDuplicatePayload } from "../utils/noticeDuplicate";
import { useQueryClient } from "@tanstack/react-query";
import { DASHBOARD_QUERY_KEY } from "../hooks/useDashboardData";
import PageContainer from "../components/PageContainer";
import PageSection from "../components/PageSection";
import { useNotices } from "../hooks/useNotices";
import { useSubcircles } from "../hooks/useSubcircles";
import { useUsers } from "../hooks/useUsers";
import CreationWizard from "../components/CreationWizard/CreationWizard";
import VideoPlayerModal from "../components/VideoPlayerModal";
import { useRegisterPageActions } from "../context/PageActionsContext";
import { DASHBOARD_TUTORIAL_VIDEOS } from "../constants/dashboardTutorialVideos";
import ViewNoticesSkeleton from "../components/ViewNoticesSkeleton";
import {
  DraftsList,
  NoticeFilterBar,
  NoticeFeedTabs,
  CancelScheduledNoticeModal,
  PublishScheduledNoticeModal,
  DeleteDraftModal,
  PublishedNoticesList,
  ScheduledNoticesList,
  SendReminderModal,
  NoticeWithType,
} from "../components/ViewNotices";
import {
  filterNoticesForFeed,
  getReadableSenderName,
  parseNoticeFilterParams,
  parseNoticeTabParam,
  splitNoticesForFeed,
  type NoticeFeedTab,
} from "../utils/noticeFeedFilters";
import { useScheduledNoticeActions } from "../hooks/useScheduledNoticeActions";
import { useMobileFabVisibility } from "../hooks/useMobileFabVisibility";
import { usePollNoticesWhileVideoProcessing } from "../hooks/usePollNoticesWhileVideoProcessing";
import { mobileFabWithScrollVisibility } from "../styles/managementShared";
import { parseCircleFilterParam } from "../utils/circleNavigation";
import { canConfirmDraftRemoval } from "../domain/notices/draftRemoval";

const ViewNotices: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const mobileFabVisible = useMobileFabVisibility(isMobile);
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  // Filter state
  const [activeTab, setActiveTab] = useState<NoticeFeedTab>(
    () => parseNoticeTabParam(searchParams) ?? "sent"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCircle, setSelectedCircle] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedSender, setSelectedSender] = useState<string>("all");

  // Draft editing state
  const [modalOpen, setModalOpen] = useState(false);
  const [draftData, setDraftData] = useState<ReturnType<
    typeof buildDraftRestorePayload
  > | null>(null);

  // Delete draft state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<NoticeWithType | null>(
    null
  );
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [isMobileQuickMenuOpen, setIsMobileQuickMenuOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [noticeToRemind, setNoticeToRemind] = useState<NoticeWithType | null>(
    null
  );
  const [sendingReminder, setSendingReminder] = useState(false);

  // Data hooks
  const { data: notices, isLoading, error, refetch } = useNotices();
  const scheduledActions = useScheduledNoticeActions(() => void refetch());
  const { subcircles } = useSubcircles();
  const { data: orgUsers = [] } = useUsers();

  useEffect(() => {
    const circleId = parseCircleFilterParam(searchParams);
    if (!circleId) return;
    const isKnownSubcircle = subcircles.some((s) => s.circleId === circleId);
    if (isKnownSubcircle) {
      setSelectedCircle(circleId);
    }
  }, [searchParams, subcircles]);

  // Ask Noticeboard links here with the Type or Sender filter set
  useEffect(() => {
    const { type, sender } = parseNoticeFilterParams(searchParams);
    if (type) setSelectedType(type);
    if (sender) setSelectedSender(sender);
  }, [searchParams]);

  // Derived data
  const uniqueSenders = useMemo(() => {
    if (!notices) return [];
    return notices
      .filter((notice) => !notice.isDeleted && !notice.isDraft)
      .map((notice) => getReadableSenderName(notice.sender, user))
      .filter((sender, index, arr) => arr.indexOf(sender) === index)
      .sort();
  }, [notices, user]);

  const { drafts, publishedNotices, scheduledNotices } = useMemo(
    () => splitNoticesForFeed((notices ?? []) as NoticeWithType[]),
    [notices]
  );

  const feedFilters = useMemo(
    () => ({
      searchQuery,
      selectedCircle,
      selectedType,
      selectedSender,
    }),
    [searchQuery, selectedCircle, selectedType, selectedSender]
  );

  const filteredPublishedNotices = useMemo(() => {
    return filterNoticesForFeed(publishedNotices, feedFilters, user);
  }, [publishedNotices, feedFilters, user]);

  const filteredScheduledNotices = useMemo(() => {
    return filterNoticesForFeed(scheduledNotices, feedFilters, user);
  }, [scheduledNotices, feedFilters, user]);

  const sortedSentNotices = useMemo(() => {
    return filteredPublishedNotices
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
  }, [filteredPublishedNotices]);

  const sortedScheduledNotices = useMemo(() => {
    return filteredScheduledNotices.slice();
  }, [filteredScheduledNotices]);

  const activeTabNotices =
    activeTab === "scheduled" ? sortedScheduledNotices : sortedSentNotices;

  usePollNoticesWhileVideoProcessing(activeTabNotices);

  // Once loaded, an empty tab gives way to the other
  useEffect(() => {
    if (!notices) return;
    if (activeTab === "scheduled" && scheduledNotices.length === 0) {
      setActiveTab("sent");
    } else if (
      activeTab === "sent" &&
      publishedNotices.length === 0 &&
      scheduledNotices.length > 0
    ) {
      setActiveTab("scheduled");
    }
  }, [notices, activeTab, scheduledNotices.length, publishedNotices.length]);

  // Handlers
  const handleNoticeClick = useCallback(
    (notice: NoticeWithType) => {
      if (notice.isDeleted) return;

    if (notice.isDraft) {
      setDraftData(
        buildDraftRestorePayload({
          ...notice,
          noticeId: notice.noticeId,
        }),
      );
      setModalOpen(true);
      return;
    }
    
    navigate(`/notice/${notice.noticeId}`, { state: { notice } });
    },
    [navigate]
  );

  const handleCreateNotice = useCallback(() => {
    navigate("/create-notice");
  }, [navigate]);
  const handleOpenTypedNotice = useCallback(
    (type: "info" | "image_notice" | "image" | "custom_event") => {
      navigate(`/create-notice?type=${type}`, {
        state: { returnTo: `${location.pathname}${location.search}` },
      });
    },
    [navigate, location.pathname, location.search]
  );

  const handleDeleteDraft = useCallback(
    (notice: NoticeWithType, e: React.MouseEvent) => {
      e.stopPropagation();
    setDraftToDelete(notice);
    setDeleteModalOpen(true);
    setDeleteConfirmationText("");
    },
    []
  );

  const confirmDeleteDraft = useCallback(async () => {
    if (!draftToDelete?.noticeId || !token || !user?.schoolId) return;
    if (!canConfirmDraftRemoval(draftToDelete.title, deleteConfirmationText)) return;

    try {
      await api.notices.delete(draftToDelete.noticeId, user.schoolId, token);
      setDeleteModalOpen(false);
      setDraftToDelete(null);
      setDeleteConfirmationText("");

      queryClient.setQueryData(["notices", user.schoolId], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter(
          (n: any) => n.noticeId !== draftToDelete.noticeId
        );
      });

      await queryClient.refetchQueries({ queryKey: ["notices"] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    } catch (error) {
      console.error("Failed to delete draft:", error);
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    }
  }, [draftToDelete, deleteConfirmationText, token, user, queryClient]);

  const handleDuplicateClick = useCallback(
    (notice: NoticeWithType, e: React.MouseEvent) => {
      e.stopPropagation();
      if (notice.isDeleted) return;
      navigate("/create-notice", {
        state: {
          prefillNotice: buildNoticeDuplicatePayload(notice),
          returnTo: `${location.pathname}${location.search}`,
        },
      });
    },
    [navigate, location.pathname, location.search]
  );

  const handleSendReminderClick = useCallback(
    (notice: NoticeWithType, e: React.MouseEvent) => {
      e.stopPropagation();
      setNoticeToRemind(notice);
      setReminderModalOpen(true);
    },
    []
  );

  const confirmSendReminder = useCallback(async () => {
    if (!noticeToRemind?.noticeId || !token) return;
    setSendingReminder(true);
    try {
      await api.notices.create(
        buildManualReminderNoticePayload(
          noticeToRemind
        ) as Parameters<typeof api.notices.create>[0],
        token
      );
      setReminderModalOpen(false);
      setNoticeToRemind(null);
      showMessage({
        message: "Reminder sent. Parents will receive a new notice and push.",
        severity: "success",
      });
      await queryClient.refetchQueries({ queryKey: ["notices"] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    } catch (err) {
      console.error("Failed to send reminder:", err);
      showMessage({
        message:
          err instanceof Error
            ? err.message
            : "Could not send reminder. Please try again.",
        severity: "error",
      });
    } finally {
      setSendingReminder(false);
    }
  }, [noticeToRemind, token, queryClient, showMessage]);

  const handleClearFilters = useCallback(() => {
    setSelectedCircle("all");
    setSelectedType("all");
    setSelectedSender("all");
    setSearchQuery("");
  }, []);

  const senderNameResolver = useCallback(
    (sender: string | undefined) => getReadableSenderName(sender, user),
    [user]
  );

  const mobileDrawerQuickActions = useMemo(
    () => [
      {
        label: "Send",
        icon: <SendIcon />,
        iconBg: theme.palette.cards.tealBg,
        iconColor: theme.palette.primary.main,
        onClick: () => handleOpenTypedNotice("info"),
      },
      {
        label: "Image",
        icon: <ImageIcon />,
        iconBg: "rgba(251, 113, 133, 0.12)",
        iconColor: "#fb7185",
        onClick: () => handleOpenTypedNotice("image_notice"),
      },
      {
        label: "Attachment",
        icon: <AttachFileIcon />,
        iconBg: theme.palette.cards.orangeBg,
        iconColor: theme.palette.warning.main,
        onClick: () => handleOpenTypedNotice("image"),
      },
      {
        label: "Add Event",
        icon: <EventIcon />,
        iconBg: theme.palette.template.eventLight,
        iconColor: theme.palette.colors.green,
        onClick: () => handleOpenTypedNotice("custom_event"),
      },
    ],
    [handleOpenTypedNotice, theme]
  );

  // Register page actions
  const pageActions = useMemo(
    () => ({
      onMainAction: handleCreateNotice,
      onVideoPlay: () =>
        setSelectedVideo({
          videoPath: DASHBOARD_TUTORIAL_VIDEOS.createNotice.path,
          title: DASHBOARD_TUTORIAL_VIDEOS.createNotice.title,
        }),
      count: publishedNotices.length,
    }),
    [handleCreateNotice, publishedNotices.length]
  );
  useRegisterPageActions(pageActions);

  // Early returns
  if (!user?.schoolId || !token) {
    return (
      <Box sx={{ textAlign: "center", p: 6, maxWidth: 600, mx: "auto" }}>
        <Typography color="text.secondary" variant="h6">
          Please log in to view notices
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return <ViewNoticesSkeleton />;
  }

  return (
    <PageContainer sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
      {/* Drafts */}
      <DraftsList
        drafts={drafts}
        onDraftClick={handleNoticeClick}
        onDeleteDraft={handleDeleteDraft}
      />

      {/* Feed tabs, metrics, filters, and list */}
      {notices &&
        notices.length > 0 &&
        (publishedNotices.length > 0 || scheduledNotices.length > 0) && (
          <>
            <NoticeFeedTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              scheduledCount={scheduledNotices.length}
              sentCount={publishedNotices.length}
            />

            <NoticeFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              selectedCircle={selectedCircle}
              onCircleChange={setSelectedCircle}
              selectedSender={selectedSender}
              onSenderChange={setSelectedSender}
              activeTab={activeTab}
              subcircles={subcircles}
              uniqueSenders={uniqueSenders}
              filteredCount={activeTabNotices.length}
              onClearFilters={handleClearFilters}
            />
          </>
        )}

      {/* Content */}
      {error && <Alert severity="error">Failed to load notices.</Alert>}
      {!notices || notices.length === 0 ? (
        <PageSection>
          <EmptyState
            title="Create Your First Notice in under 60 seconds"
            description="Send announcements, reminders, or urgent updates instantly, no printing, no  groups, no delays."
            imageSrc="/notices/notice.webp"
            imageAlt="Notices illustration"
            buttonText="Create Notice"
            onButtonClick={handleCreateNotice}
            benefits={[
              "Keep users and staff informed",
              "Share updates instantly",
              "Reach everyone at once",
              "Strengthen your school community",
            ]}
            benefitsTitle="Why send notices?"
            onBenefitsTitleVideoClick={() =>
              setSelectedVideo({
                videoPath: DASHBOARD_TUTORIAL_VIDEOS.createNotice.path,
                title: DASHBOARD_TUTORIAL_VIDEOS.createNotice.title,
              })
            }
            howItWorks={[
              "Write your notice — add text, images, or attachments",
              "Choose which circles should receive it",
              "Tap publish — everyone is notified instantly",
            ]}
            howItWorksTitle="⚡ How it works (takes less than 60 seconds)"
          />
        </PageSection>
      ) : activeTab === "scheduled" ? (
        <ScheduledNoticesList
          notices={sortedScheduledNotices}
          actionNoticeId={scheduledActions.actionNoticeId}
          rescheduleTarget={
            scheduledActions.rescheduleTarget as NoticeWithType | null
          }
          savingReschedule={scheduledActions.savingReschedule}
          onNoticeClick={handleNoticeClick}
          onReschedule={scheduledActions.setRescheduleTarget}
          onRescheduleClose={() => scheduledActions.setRescheduleTarget(null)}
          onRescheduleSave={scheduledActions.handleRescheduleSave}
          onCancel={scheduledActions.handleCancelClick}
          onPublishNow={scheduledActions.handlePublishNowClick}
          getReadableSenderName={senderNameResolver}
          schoolName={user?.schoolName || "School"}
          subcircles={subcircles}
          orgMemberCount={orgUsers.length}
        />
      ) : (
        <PublishedNoticesList
          notices={sortedSentNotices}
          onNoticeClick={handleNoticeClick}
          onDuplicateClick={handleDuplicateClick}
          onSendReminderClick={handleSendReminderClick}
          sendingReminderNoticeId={
            sendingReminder ? noticeToRemind?.noticeId ?? null : null
          }
          getReadableSenderName={senderNameResolver}
          schoolName={user?.schoolName || "School"}
          subcircles={subcircles}
          orgMemberCount={orgUsers.length}
        />
      )}

      {/* Draft editing modal */}
      {modalOpen && draftData && (
        <CreationWizard
          noticeType={draftData.noticeType}
          onClose={() => {
            setModalOpen(false);
            setDraftData(null);
          }}
          open={modalOpen}
          initialFormData={draftData.initialFormData}
          initialEventDate={
            draftData.initialFormData?.eventStartDate || null
          }
          onDataRefresh={() => {
            refetch();
            // Don't close modal here - let success screen show, then user clicks Continue -> onClose
          }}
        />
      )}

      <SendReminderModal
        open={reminderModalOpen}
        onClose={() => {
          if (sendingReminder) return;
          setReminderModalOpen(false);
          setNoticeToRemind(null);
        }}
        noticeTitle={noticeToRemind?.title || ""}
        onConfirm={confirmSendReminder}
        loading={sendingReminder}
      />

      <CancelScheduledNoticeModal
        open={scheduledActions.cancelTarget !== null}
        onClose={() => {
          if (scheduledActions.cancelling) return;
          scheduledActions.setCancelTarget(null);
        }}
        noticeTitle={scheduledActions.cancelTarget?.title || ""}
        onConfirm={() => void scheduledActions.handleCancelConfirm()}
        loading={scheduledActions.cancelling}
      />

      <PublishScheduledNoticeModal
        open={scheduledActions.publishTarget !== null}
        onClose={() => {
          if (scheduledActions.publishing) return;
          scheduledActions.setPublishTarget(null);
        }}
        noticeTitle={scheduledActions.publishTarget?.title || ""}
        onConfirm={() => void scheduledActions.handlePublishNowConfirm()}
        loading={scheduledActions.publishing}
      />

      {/* Delete draft confirmation */}
      <DeleteDraftModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDraftToDelete(null);
          setDeleteConfirmationText("");
        }}
        draftTitle={draftToDelete?.title}
        confirmationText={deleteConfirmationText}
        onConfirmationTextChange={setDeleteConfirmationText}
        onConfirm={confirmDeleteDraft}
      />

      {isMobile && (
        <Fab
          color="primary"
          aria-label="Open quick actions"
          onClick={() => setIsMobileQuickMenuOpen(true)}
          sx={mobileFabWithScrollVisibility(
            {
              position: "fixed",
              right: 20,
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
              width: 58,
              height: 58,
              boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.35)}`,
              zIndex: theme.zIndex.speedDial,
            },
            mobileFabVisible,
          )}
        >
          <AddIcon sx={{ fontSize: 30 }} />
        </Fab>
      )}

      <Drawer
        anchor="bottom"
        open={isMobileQuickMenuOpen}
        onClose={() => setIsMobileQuickMenuOpen(false)}
        transitionDuration={{ enter: 180, exit: 100 }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 40px)",
            px: 2,
            pt: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
          <Box
            sx={{
              width: 44,
              height: 5,
              borderRadius: 999,
              bgcolor: "grey.300",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {mobileDrawerQuickActions.map((action) => (
            <Paper
              key={`mobile-create-${action.label}`}
              elevation={0}
              onClick={() => {
                setIsMobileQuickMenuOpen(false);
                action.onClick();
              }}
              sx={{
                p: 1,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                minHeight: 124,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  bgcolor: action.iconBg,
                  color: action.iconColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                  "& svg": {
                    fontSize: 30,
                  },
                }}
              >
                {action.icon}
              </Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                {action.label}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Drawer>

      <VideoPlayerModal
        open={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        videoPath={selectedVideo?.videoPath || ""}
        title={selectedVideo?.title || ""}
      />
    </PageContainer>
  );
};

export default ViewNotices;
